import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { maintenanceAPI } from "../../utils/api";
import Layout from "../common/Layout";
import AssignedTasks from "./AssignedTasks";

const WorkerDashboard = () => {
  const { userProfile, logout } = useAuth();
  const [stats, setStats] = useState({
    totalAssigned: 0,
    pending: 0,
    inProgress: 0,
    completed: 0,
    averageRating: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await maintenanceAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching worker stats:", error);
      setError("Failed to load dashboard statistics");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStats();
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout title="Worker Dashboard">
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1" gutterBottom>
            Worker Dashboard
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title="Refresh Statistics">
              <IconButton onClick={handleRefresh} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Logout">
              <IconButton
                onClick={logout}
                color="error"
                sx={{
                  border: 1,
                  borderColor: "error.main",
                  "&:hover": {
                    backgroundColor: "error.light",
                    color: "white",
                  },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Welcome Message */}
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Welcome back, {userProfile?.name}!
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      Total Assigned
                    </Typography>
                    <Typography variant="h4">{stats.totalAssigned}</Typography>
                  </Box>
                  <AssignmentIcon color="primary" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      Pending Tasks
                    </Typography>
                    <Typography variant="h4">{stats.pending}</Typography>
                  </Box>
                  <ScheduleIcon color="warning" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      In Progress
                    </Typography>
                    <Typography variant="h4">{stats.inProgress}</Typography>
                  </Box>
                  <TrendingUpIcon color="info" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      color="text.secondary"
                      gutterBottom
                      variant="body2"
                    >
                      Completed
                    </Typography>
                    <Typography variant="h4">{stats.completed}</Typography>
                  </Box>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Performance Rating */}
        {stats.averageRating > 0 && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Performance Rating
                  </Typography>
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h3" color="primary">
                      {stats.averageRating.toFixed(1)}
                    </Typography>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Average satisfaction rating
                      </Typography>
                      <Chip
                        label={
                          stats.averageRating >= 4.5
                            ? "Excellent"
                            : stats.averageRating >= 4
                            ? "Very Good"
                            : stats.averageRating >= 3.5
                            ? "Good"
                            : "Needs Improvement"
                        }
                        color={
                          stats.averageRating >= 4.5
                            ? "success"
                            : stats.averageRating >= 4
                            ? "primary"
                            : stats.averageRating >= 3.5
                            ? "info"
                            : "warning"
                        }
                        size="small"
                      />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Assigned Tasks Component */}
        <AssignedTasks />
      </Box>
    </Layout>
  );
};

export default WorkerDashboard;
